export interface ServerSentEvent {
    event: string;
    data: string;
}

/** A network chunk can contain part of a line, or many complete events. */
export async function* readServerSentEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<ServerSentEvent> {
    const reader = body.getReader();
    const decoder = new TextDecoder('utf-8', { fatal: true });
    let buffer = '';
    let event = '';
    let data: string[] = [];
    try {
        while (true) {
            const { value, done } = await reader.read();
            buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
            let end: number;
            while ((end = buffer.search(/[\r\n]/)) !== -1) {
                // A trailing CR may be the first half of a fragmented CRLF.
                if (buffer[end] === '\r' && end === buffer.length - 1 && !done) break;
                const line = buffer.slice(0, end);
                const separatorLength = buffer[end] === '\r' && buffer[end + 1] === '\n' ? 2 : 1;
                buffer = buffer.slice(end + separatorLength);
                if (line === '') {
                    if (data.length) yield { event: event || 'message', data: data.join('\n') };
                    event = '';
                    data = [];
                } else if (!line.startsWith(':')) {
                    const colon = line.indexOf(':');
                    const name = colon < 0 ? line : line.slice(0, colon);
                    const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
                    if (name === 'event') event = value;
                    if (name === 'data') data.push(value);
                }
            }
            // EOF does not dispatch an unfinished event.
            if (done) return;
        }
    } finally {
        // Stop receiving after a terminal event; this does not cancel the server task.
        void reader.cancel().catch(() => undefined);
        reader.releaseLock();
    }
}

export interface SummaryUpdateResult {
    summary: string;
    saved: boolean;
    mode?: string;
    task_id?: string;
    title?: string;
    summary_id?: string | number | null;
    [key: string]: unknown;
}

export type SummaryUpdateEvent =
    | { event: 'status'; data: { phase: string; task_id?: string } }
    | { event: 'source'; data: Record<string, unknown> }
    | { event: 'preview'; data: { summary: string; task_id?: string } }
    | { event: 'result'; data: SummaryUpdateResult };

export class SummaryUpdateError extends Error {
    constructor(message: string, public code: string, public discardPreview = true, public metadata?: Record<string, unknown>) {
        super(message);
        this.name = 'SummaryUpdateError';
    }
}

export class SummaryUpdateInterruptedError extends Error {
    constructor() {
        super('Verbinding onderbroken. De uitkomst is onbekend; de server kan nog bezig zijn. De opgeslagen versie wordt opnieuw geladen.');
        this.name = 'SummaryUpdateInterruptedError';
    }
}

export class SummaryRequestNotStartedError extends Error {}

export async function* readSummaryUpdateEvents(response: Response): AsyncGenerator<SummaryUpdateEvent> {
    if (!response.ok) throw new SummaryUpdateError(`HTTP ${response.status}: ${response.statusText}`, 'http_error', true, { http_status: response.status });
    if (response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase() !== 'text/event-stream' || !response.body) {
        void response.body?.cancel().catch(() => undefined);
        throw new SummaryUpdateInterruptedError();
    }
    try {
        for await (const message of readServerSentEvents(response.body)) {
            if (!['status', 'source', 'preview', 'result', 'error'].includes(message.event)) continue;
            const data = JSON.parse(message.data);
            if (!data || typeof data !== 'object' || Array.isArray(data)) throw new SummaryUpdateInterruptedError();
            if (message.event === 'error') {
                throw new SummaryUpdateError(
                    typeof data.message === 'string' ? data.message : typeof data.error === 'string' ? data.error : 'De verslagwijziging is mislukt.',
                    typeof data.code === 'string' ? data.code : typeof data.error_code === 'string' ? data.error_code : 'stream_error',
                    data.discard_preview === true,
                    data,
                );
            }
            if (message.event === 'preview' || message.event === 'result') {
                if (typeof data.summary !== 'string') throw new SummaryUpdateInterruptedError();
            }
            if (message.event === 'result' && data.saved !== true && !(data.mode === 'no_change' && data.saved === false)) {
                throw new SummaryUpdateInterruptedError();
            }
            yield { event: message.event, data } as SummaryUpdateEvent;
            if (message.event === 'result') return;
        }
    } catch (error) {
        if (error instanceof SummaryUpdateError || error instanceof SummaryUpdateInterruptedError) throw error;
        throw new SummaryUpdateInterruptedError();
    }
    throw new SummaryUpdateInterruptedError();
}
